/**
 * Custom Vertical Gallery Scroller
 * --------------------------------
 * Adds a Gymshark-style vertical scroll bar (up/down arrows + draggable thumb)
 * to the desktop product gallery on the product page.
 *
 * Scroll model (see snippets/custom-vertical-gallery-scroller.liquid for the CSS):
 *   - The desktop gallery container `.product-desktop-media--vgs` has a fixed height
 *     and is sticky. The inner `<product-media>` grid is the actual scroll container
 *     (overflow-y: auto), so the images scroll *inside* the gallery.
 *   - This control drives that internal scroll: the thumb reflects scroll progress,
 *     the arrows step to the previous/next media item, and the thumb can be dragged.
 *   - Scroll isolation + chaining is handled natively: wheeling over the gallery
 *     scrolls only the gallery; when it reaches the bottom the page (product details)
 *     scrolls. Wheeling over the details scrolls the page only.
 *   - Arrows are disabled at the scroll boundaries (top / bottom).
 *
 * Desktop only: the host lives inside `.product-desktop-media`, which is display:none
 * below 768px; a matchMedia guard also stops the math from running on mobile.
 *
 * Non-invasive: it only reads/sets scroll position on its own gallery container.
 * It never mutates the gallery markup or interferes with Aurora's own scripts.
 */
(function () {
  'use strict';

  // Avoid redefining if this script is somehow included more than once.
  if (window.customElements && customElements.get('custom-gallery-scroller')) return;

  var DESKTOP_QUERY = '(min-width: 768px)';

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class CustomGalleryScroller extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.track = this.querySelector('[data-vgs-track]');
      this.thumb = this.querySelector('[data-vgs-thumb]');
      this.rail = this.querySelector('.custom-vgs__rail');
      this.upBtn = this.querySelector('[data-vgs-up]');
      this.downBtn = this.querySelector('[data-vgs-down]');

      this.mediaContainer = this.closest('.product-desktop-media');
      // The inner <product-media> grid is the scrollable element.
      this.scrollEl = this.mediaContainer
        ? this.mediaContainer.querySelector('product-media')
        : null;

      // Bail out gracefully if the expected structure isn't present.
      if (!this.scrollEl || !this.track || !this.thumb) return;

      this._ticking = false;
      this._dragging = false;
      this._smoothTarget = this.scrollEl.scrollTop;
      this._smoothing = false;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._animateScroll = this._animateScroll.bind(this);
      this._onTrackPointerDown = this._onTrackPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this.scrollEl.addEventListener('scroll', this._onScroll, { passive: true });
      // Smooth wheel scrolling (skip when the user prefers reduced motion).
      if (!this.reduceMotion.matches) {
        this.scrollEl.addEventListener('wheel', this._onWheel, { passive: false });
      }
      window.addEventListener('resize', this._onResize, { passive: true });

      if (this.upBtn) this.upBtn.addEventListener('click', () => this.step(-1));
      if (this.downBtn) this.downBtn.addEventListener('click', () => this.step(1));
      this.track.addEventListener('pointerdown', this._onTrackPointerDown);

      // Initial paint + re-measure once images have loaded, since the gallery's
      // scrollHeight drives all the math.
      this.update();
      requestAnimationFrame(() => this.update());
      window.addEventListener('load', () => this.update());

      // Re-measure when images inside the gallery finish loading.
      this._observeImages();
    }

    disconnectedCallback() {
      if (this.scrollEl) {
        this.scrollEl.removeEventListener('scroll', this._onScroll);
        this.scrollEl.removeEventListener('wheel', this._onWheel);
      }
      window.removeEventListener('resize', this._onResize);
    }

    get items() {
      return Array.prototype.slice.call(
        this.scrollEl.querySelectorAll('.product-media-grid__item')
      );
    }

    /** Only active when on desktop and actually visible (not display:none). */
    isActive() {
      return this.desktop.matches && this.offsetParent !== null;
    }

    _observeImages() {
      var self = this;
      var imgs = this.scrollEl.querySelectorAll('img');
      Array.prototype.forEach.call(imgs, function (img) {
        if (!img.complete) {
          img.addEventListener('load', function () { self.update(); }, { once: true });
        }
      });
    }

    _onScroll() {
      // Keep the smooth-scroll target in sync with any scroll we didn't animate
      // (drag, arrow buttons, keyboard), so the next wheel starts from here.
      if (!this._smoothing) this._smoothTarget = this.scrollEl.scrollTop;

      if (this._ticking) return;
      this._ticking = true;
      var self = this;
      requestAnimationFrame(function () {
        self._ticking = false;
        self.update();
      });
    }

    _onResize() {
      this.update();
    }

    /**
     * Smooth wheel scrolling: accumulate wheel delta into a target and ease the
     * gallery's scrollTop toward it. At the top/bottom edge we let the event
     * through so the page (product details) keeps scrolling naturally.
     */
    _onWheel(event) {
      if (!this.isActive()) return;

      var el = this.scrollEl;
      var max = el.scrollHeight - el.clientHeight;
      var delta = event.deltaY;
      // deltaMode 1 = lines, 2 = pages; normalise to pixels.
      if (event.deltaMode === 1) delta *= 16;
      else if (event.deltaMode === 2) delta *= el.clientHeight;

      var atTop = this._smoothTarget <= 0;
      var atBottom = this._smoothTarget >= max - 1;
      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        return; // let the page scroll
      }

      event.preventDefault();
      this._smoothTarget = clamp(this._smoothTarget + delta, 0, max);
      if (!this._smoothing) {
        this._smoothing = true;
        requestAnimationFrame(this._animateScroll);
      }
    }

    _animateScroll() {
      var el = this.scrollEl;
      var current = el.scrollTop;
      var diff = this._smoothTarget - current;
      if (Math.abs(diff) < 0.5) {
        el.scrollTop = this._smoothTarget;
        this._smoothing = false;
        return;
      }
      el.scrollTop = current + diff * 0.18; // easing factor (higher = snappier)
      requestAnimationFrame(this._animateScroll);
    }

    /** Internal-scroll geometry of the gallery container. */
    metrics() {
      var el = this.scrollEl;
      var max = el.scrollHeight - el.clientHeight;
      var progress = max > 0 ? el.scrollTop / max : 0;
      return { max: max, progress: progress, scrollTop: el.scrollTop };
    }

    update() {
      if (!this.isActive()) return;

      var m = this.metrics();

      // Hide the whole control when the gallery is too short to scroll.
      if (m.max <= 8) {
        this.setAttribute('data-vgs-idle', '');
      } else {
        this.removeAttribute('data-vgs-idle');
      }

      var b = this._thumbBounds();
      var offset = b.top + m.progress * b.travel;
      // translateX(-50%) keeps the thumb centred horizontally; translateY moves it.
      this.thumb.style.transform =
        'translateX(-50%) translateY(' + offset.toFixed(2) + 'px)';

      // Disable the arrows at the scroll boundaries.
      this._setDisabled(this.upBtn, m.scrollTop <= 1);
      this._setDisabled(this.downBtn, m.scrollTop >= m.max - 1);
    }

    _setDisabled(btn, disabled) {
      if (!btn) return;
      btn.disabled = !!disabled;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    scrollBehavior() {
      return this.reduceMotion.matches ? 'auto' : 'smooth';
    }

    /** Step to the previous (-1) or next (+1) media item within the gallery. */
    step(direction) {
      if (!this.isActive()) return;
      var items = this.items;
      if (!items.length) return;

      var el = this.scrollEl;
      var containerTop = el.getBoundingClientRect().top;

      // The "current" item is the last one whose top has passed the container top.
      var current = 0;
      items.forEach(function (item, index) {
        var rel = item.getBoundingClientRect().top - containerTop;
        if (rel <= 4) current = index;
      });

      var target = clamp(current + direction, 0, items.length - 1);
      var top =
        items[target].getBoundingClientRect().top - containerTop + el.scrollTop;
      el.scrollTo({ top: top, behavior: this.scrollBehavior() });
    }

    /**
     * The thumb's travel, bounded to the rail (the visible inset line) so the
     * thumb rides from the rail top to the rail bottom and never spills past the
     * rounded ends of the track. Falls back to the full track if no rail.
     */
    _thumbBounds() {
      // The thumb lives inside the rail, so its top:0 already sits at the rail
      // top — start offset is 0. Travel is the rail's inner height minus the
      // thumb height, so the thumb's bottom stops exactly at the rail bottom.
      var container = this.rail || this.track;
      var travel = container.clientHeight - this.thumb.offsetHeight;
      return { top: 0, travel: Math.max(travel, 0) };
    }

    /** Map a 0..1 ratio to the gallery's internal scroll position. */
    _scrollToRatio(ratio) {
      var el = this.scrollEl;
      var max = el.scrollHeight - el.clientHeight;
      el.scrollTop = clamp(ratio, 0, 1) * max;
    }

    _ratioFromPointer(clientY) {
      // Map the pointer within the rail (where the thumb actually travels).
      var container = this.rail || this.track;
      var rect = container.getBoundingClientRect();
      var b = this._thumbBounds();
      var y = clientY - rect.top - this.thumb.offsetHeight / 2;
      return b.travel > 0 ? clamp(y / b.travel, 0, 1) : 0;
    }

    _onTrackPointerDown(event) {
      if (!this.isActive()) return;
      event.preventDefault();
      this._dragging = true;
      this.setAttribute('data-vgs-dragging', '');
      if (this.track.setPointerCapture) {
        this.track.setPointerCapture(event.pointerId);
      }
      this._scrollToRatio(this._ratioFromPointer(event.clientY));

      this.track.addEventListener('pointermove', this._onPointerMove);
      this.track.addEventListener('pointerup', this._onPointerUp);
      this.track.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this._dragging) return;
      this._scrollToRatio(this._ratioFromPointer(event.clientY));
    }

    _onPointerUp(event) {
      this._dragging = false;
      this.removeAttribute('data-vgs-dragging');
      if (this.track.releasePointerCapture) {
        try { this.track.releasePointerCapture(event.pointerId); } catch (e) {}
      }
      this.track.removeEventListener('pointermove', this._onPointerMove);
      this.track.removeEventListener('pointerup', this._onPointerUp);
      this.track.removeEventListener('pointercancel', this._onPointerUp);
    }
  }

  customElements.define('custom-gallery-scroller', CustomGalleryScroller);
})();
